<template>
  <div class="container">
    <h1>Authentication Testing</h1>
    
    <AuthStatus />
    
    <div class="test-section">
      <h2>Test API Requests</h2>
      <p>Use the buttons below to test API requests with the current authentication token.</p>
      
      <div class="test-actions">
        <button class="btn test-btn" @click="testGetRequest">
          Test GET Request
        </button>
        <button class="btn test-btn" @click="testPostRequest">
          Test POST Request
        </button>
      </div>
      
      <div v-if="testResult" class="test-result">
        <h3>Test Result</h3>
        <pre>{{ testResult }}</pre>
      </div>
      
      <div v-if="testError" class="test-error">
        <h3>Test Error</h3>
        <pre>{{ testError }}</pre>
      </div>
    </div>
    
    <div class="manual-section">
      <h2>Manual Token Testing</h2>
      <p>Enter a URL and test it with your current token.</p>
      
      <div class="input-group">
        <label for="test-url">API URL:</label>
        <input 
          id="test-url" 
          v-model="testUrl" 
          type="text" 
          placeholder="Enter API URL (e.g. http://localhost:8000/api/endpoint)"
        />
      </div>
      
      <div class="test-actions">
        <button class="btn test-btn" @click="testCustomRequest('GET')">
          GET
        </button>
        <button class="btn test-btn" @click="testCustomRequest('POST')">
          POST
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AuthStatus from '../components/AuthStatus.vue'

export default {
  name: 'TestAuth',
  components: {
    AuthStatus
  },
  data() {
    return {
      testResult: null,
      testError: null,
      testUrl: ''
    }
  },
  computed: {
    apiUrl() {
      return import.meta.env.VITE_API_URL || 'http://localhost:8000/api'
    }
  },
  methods: {
    async testGetRequest() {
      this.clearResults()
      try {
        // Replace with an actual endpoint from your API
        const response = await axios.get(`${this.apiUrl}/characters/`)
        this.testResult = JSON.stringify(response.data, null, 2)
      } catch (error) {
        this.handleError(error)
      }
    },
    
    async testPostRequest() {
      this.clearResults()
      try {
        // Replace with an actual endpoint from your API
        const response = await axios.post(`${this.apiUrl}/characters/conversations/`, {
          // Add any required data for your API
          test: true
        })
        this.testResult = JSON.stringify(response.data, null, 2)
      } catch (error) {
        this.handleError(error)
      }
    },
    
    async testCustomRequest(method) {
      if (!this.testUrl) {
        this.testError = 'Please enter a URL'
        return
      }
      
      this.clearResults()
      try {
        let response
        if (method === 'GET') {
          response = await axios.get(this.testUrl)
        } else if (method === 'POST') {
          response = await axios.post(this.testUrl, { test: true })
        }
        
        this.testResult = JSON.stringify(response.data, null, 2)
      } catch (error) {
        this.handleError(error)
      }
    },
    
    handleError(error) {
      console.error('API request error:', error)
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        this.testError = `Status: ${error.response.status}\nData: ${JSON.stringify(error.response.data, null, 2)}`
      } else if (error.request) {
        // The request was made but no response was received
        this.testError = 'No response received from server'
      } else {
        // Something happened in setting up the request that triggered an Error
        this.testError = error.message
      }
    },
    
    clearResults() {
      this.testResult = null
      this.testError = null
    }
  }
}
</script>

<style scoped>
.container {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 24px;
  text-align: center;
}

h2 {
  margin-top: 30px;
  margin-bottom: 15px;
  font-size: 20px;
}

h3 {
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 16px;
}

p {
  margin-bottom: 20px;
  color: var(--tg-theme-hint-color);
}

.test-section, .manual-section {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
}

.test-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.test-btn {
  flex: 1;
  padding: 10px;
  font-size: 14px;
}

.test-result, .test-error {
  padding: 15px;
  border-radius: 8px;
  margin-top: 20px;
}

.test-result {
  background-color: rgba(76, 175, 80, 0.1);
}

.test-error {
  background-color: rgba(244, 67, 54, 0.1);
}

pre {
  white-space: pre-wrap;
  word-break: break-all;
  font-family: monospace;
  font-size: 14px;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  overflow-x: auto;
}

.input-group {
  margin-bottom: 15px;
}

.input-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.input-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
}
</style> 